import {
  Button,
  Card,
  Col,
  Divider,
  Dropdown,
  Grid,
  List,
  Progress,
  Row,
  Space,
  Tag,
  Tooltip,
} from "antd";
import React, { useContext, useEffect, useRef, useState } from "react";
import DataContext from "../../context/DataContext";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  CustomerProps,
  ProjectProps,
  TagProps,
  ToDoProps,
} from "../../interfaces/interfaces";
import { TaskList } from "../../components/task";
import {
  DrawerCreateOrUpdateProject,
  ProjectList,
} from "../../components/project";
import { ContactList, CustomerDrawer } from "../../components/customer";
import AuthContext from "../../context/AuthContext";
import { useDemoModal } from "../../hooks";
import { ModalCreateOrUpdateToDo } from "../../components/user";
import {
  PlusOutlined,
  CheckCircleOutlined,
  SortAscendingOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Base64 } from "js-base64";
import { CardTitle, TagSelector } from "../../components/ui";
import dayjs from "dayjs";
import { MenuProps } from "antd/lib";
import { SearchInput } from "../../components/functional";
import { ActivityList } from "../../components/activity";
import { TASK_INITIAL_STATE } from "../../utils/data";

export const DashboardClient = () => {
  const navigate = useNavigate();
  const { useBreakpoint } = Grid;
  const { xs } = useBreakpoint();
  const { idClient } = useParams();
  const { isDemoEnv, profileInfo, getClientInformation, onLogout } =
    useContext(AuthContext);
  const [selectedItem, setSelectedItem] = useState<[boolean, boolean]>([
    false,
    false,
  ]);
  const { DemoModal, openModalDemo } = useDemoModal();
  const [completionRate, setCompletionRate] = useState<number>(0);
  const [todoModalStatus, setTodoModalStatus] = useState<{
    open: boolean;
    create: boolean;
    update: boolean;
    payload: ToDoProps | null;
  } | null>(null);
  const [customerDrawerStatus, setCustomerDrawerStatus] = useState<{
    open: boolean;
    idCustomer: string | null;
  }>({ open: false, idCustomer: "" });
  const [tagsSelected, setTagsSelected] = useState<TagProps[]>([]);

  const {
    getData: getListData,
    contactList,
    todoList,
    projectModal,
    openItemModal,
    closeItemModal,
    resetContacts,
    handleWarning,
    handleCreateData,
    handleEditData,
    handleDeleteData,
    getCustomersListByClient,
    handleChangeProjectModalStatus,
  } = useContext(DataContext);

  const [searchParams] = useSearchParams();
  const refWarning = useRef(false);
  const [collapsedSidebar, setCollapsedSidebar] = useState<boolean>(true);

  useEffect(() => {
    if (searchParams.get("message")) {
      const message = searchParams.get("message")?.toString();
      if (message) {
        const messageDecoded = Base64.decode(message);
        if (messageDecoded === "Not Authorized") {
          onLogout();
        } else {
          !refWarning.current && handleWarning(true, messageDecoded);
          refWarning.current = true;
          navigate("/dashboard");
        }
      }
    }
  }, []);

  useEffect(() => {
    if (idClient) {
      profileInfo?._id &&
        getListData("projects-paginated", idClient, undefined, {
          assignedTo: profileInfo?._id,
          active: true,
        });

      getListData("tasks", idClient, undefined, {
        assignedTo: true,
        active: true,
      });
      getListData("activities-paginated", idClient, undefined, {
        assignedTo: true,
        active: true,
      });
      getListData("projects", idClient, undefined, {
        active: true,
      });
      getListData("project-templates", idClient, undefined, {
        active: true,
      });
      getListData("users", idClient);
      getListData("todos", idClient);
      getClientInformation(idClient, () => {});
      getCustomersListByClient({ idClient });

      getListData("customer", idClient);
    }
    resetContacts();
  }, [idClient, profileInfo]);

  useEffect(() => {
    const completedTasks = todoList.filter((task) => task.done).length;
    const totalTasks = todoList.length;
    const completionRate = totalTasks
      ? Math.round((completedTasks / totalTasks) * 100)
      : 0;
    setCompletionRate(completionRate);
  }, [todoList]);

  useEffect(() => {
    closeItemModal("task");
    closeItemModal("project");
    closeItemModal("contact");
    closeItemModal("activity");
  }, []);

  const completeTask = (idToDo: string) => {
    idClient &&
      handleEditData({
        endpoint: `todo/${idClient}/${idToDo}`,
        newData: {
          done: true,
        },
        next: () => getListData("todos", idClient),
        showAlert: false,
      });
  };

  const createOrUpdateProject = (
    values: ProjectProps,
    idClient: string,
    idProject?: string,
  ) => {
    if (projectModal && projectModal.create) {
      handleCreateData({
        endpoint: "projects",
        idClient,
        data: values,
        next: () => {
          getListData("projects-paginated", idClient, undefined, {
            assignedTo: profileInfo?._id,
            active: true,
          });
        },
      });
    } else if (projectModal?.update && idProject) {
      handleEditData({
        endpoint: `project/${idClient}/${idProject}`,
        newData: values,
        next: () => {
          getListData("projects-paginated", idClient, undefined, {
            assignedTo: profileInfo?._id,
            active: true,
          });
        },
      });
    }
    closeItemModal("project");
  };

  const handleFilterByTags = (selectedTags: TagProps[]) => {
    setTagsSelected(selectedTags);
    if (selectedTags.length > 0) {
      if (idClient) {
        getListData("tasks", idClient, undefined, {
          assignedTo: true,
          active: true,
          tags: {
            $in: selectedTags.map((tag) => tag._id),
          },
        });

        getListData("projects-paginated", idClient, undefined, {
          assignedTo: profileInfo?._id,
          active: true,
          tags: {
            $in: selectedTags.map((tag) => tag._id),
          },
        });
      }
    } else {
      idClient &&
        getListData("tasks", idClient, undefined, {
          assignedTo: true,
          active: true,
        });

      idClient &&
        getListData("projects-paginated", idClient, undefined, {
          assignedTo: profileInfo?._id,
          active: true,
        });
    }
  };

  const items: MenuProps["items"] = [
    {
      label: "Priority",
      key: "1",
      onClick: () => {
        let selected = selectedItem[0];
        idClient &&
          getListData(
            "todos",
            idClient,
            undefined,
            { sort: { priority: selected ? -1 : 1 } },
            false,
          );
        setSelectedItem((prev) => [!prev[0], prev[1]]);
      },
    },
    {
      label: "Due Date",
      key: "2",
      onClick: () => {
        let selected = selectedItem[1];
        idClient &&
          getListData("todos", idClient, undefined, {
            sort: { dueDate: selected ? -1 : 1 },
          });
        setSelectedItem((prev) => [prev[0], !prev[1]]);
      },
    },
  ];

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={24}>
          <CardTitle
            title="Dashboard"
            extra={[
              <div style={{ margin: 8 }} key="extra">
                <TagSelector
                  selectedTags={tagsSelected}
                  setSelectedTags={(selected) => handleFilterByTags(selected)}
                  filtering={true}
                />
              </div>,
            ]}
          />
        </Col>
        <Col xs={24} md={24} xl={collapsedSidebar ? 23 : 15}>
          <TaskList
            origin="dashboard"
            onAddNewTask={() =>
              openItemModal("task", true, {
                ...TASK_INITIAL_STATE,
                origin: "dashboard",
              })
            }
            onEditTask={(task) =>
              openItemModal("task", false, { ...task, origin: "dashboard" })
            }
            onDuplicateTask={(task) =>
              openItemModal("task", true, {
                ...task,
                _id: "",
                description: task.description + " (Copy)",
                origin: "dashboard",
              })
            }
            onSelectCustomer={(record) =>
              setCustomerDrawerStatus({ open: true, idCustomer: record })
            }
            onChangePage={(current, pageSize) => {
              idClient &&
                getListData("tasks", idClient, undefined, {
                  assignedTo: true,
                  active: true,
                  page: current,
                  perPage: pageSize,
                });
            }}
            onDeleteTask={(task, _, pageSize, currentPage) =>
              handleDeleteData({
                endpoint: `task/${idClient}/${task._id}`,
                next: () => {
                  idClient &&
                    getListData("tasks", idClient, undefined, {
                      assignedTo: true,
                      active: true,
                      page: currentPage,
                      perPage: pageSize,
                    });
                },
              })
            }
          />
          <ActivityList
            origin="dashboard"
            onAddNewActivity={() => openItemModal("activity", true)}
            onEditActivity={(activity) =>
              openItemModal("activity", false, {
                ...activity,
                origin: "dashboard",
              })
            }
            onDuplicateActivity={(activity) =>
              openItemModal("activity", true, {
                ...activity,
                _id: "",
                origin: "dashboard",
              })
            }
            onSelectCustomer={(record) =>
              setCustomerDrawerStatus({ open: true, idCustomer: record })
            }
            onChangePage={(current, pageSize) => {
              idClient &&
                getListData("activities-paginated", idClient, undefined, {
                  assignedTo: true,
                  active: true,
                  page: current,
                  perPage: pageSize,
                });
            }}
            onDeleteActivity={(activity, pageSize, currentPage) =>
              handleDeleteData({
                endpoint: `activity/${idClient}/${activity._id}`,
                next: () => {
                  idClient &&
                    getListData("activities-paginated", idClient, undefined, {
                      assignedTo: true,
                      active: true,
                      page: currentPage,
                      perPage: pageSize,
                    });
                },
              })
            }
          />
          <Card
            title={
              <Space>
                <div>Projects</div>

                <Tooltip title="New">
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => openItemModal("project", true)}
                  ></Button>
                </Tooltip>
              </Space>
            }
            extra={
              !xs
                ? [
                    <SearchInput
                      onSearch={(value: string) => {
                        if (value) {
                          idClient &&
                            getListData("projects-paginated", idClient, value, {
                              assignedTo: profileInfo?._id,
                              active: true,
                            });
                        } else {
                          idClient &&
                            getListData(
                              "projects-paginated",
                              idClient,
                              undefined,
                              {
                                assignedTo: profileInfo?._id,
                                active: true,
                              },
                            );
                        }
                      }}
                    />,
                  ]
                : []
            }
          >
            <ProjectList
              onSelectOne={(project) =>
                openItemModal("project", false, project)
              }
              onSelectCustomer={(record) =>
                setCustomerDrawerStatus({ open: true, idCustomer: record })
              }
              hideColumns={true}
              onChangePage={(current, pageSize) => {
                idClient &&
                  getListData("projects-paginated", idClient, undefined, {
                    assignedTo: profileInfo?._id,
                    active: true,
                    page: current,
                    perPage: pageSize,
                  });
              }}
            />
          </Card>
        </Col>
        <Col
          span={1}
          style={{
            transition: "all 0.3s",
          }}
        >
          {collapsedSidebar ? (
            <Tooltip
              title={"More Details"}
              placement="bottom"
              open // always show tooltip
            >
              <Button
                type="text"
                icon={collapsedSidebar ? <LeftOutlined /> : <RightOutlined />}
                onClick={() => setCollapsedSidebar((prev) => !prev)}
              />
            </Tooltip>
          ) : (
            <Button
              type="text"
              icon={collapsedSidebar ? <LeftOutlined /> : <RightOutlined />}
              onClick={() => setCollapsedSidebar((prev) => !prev)}
            />
          )}
        </Col>
        <Col
          xs={24}
          md={24}
          xl={8}
          style={{
            display: collapsedSidebar ? "none" : "block",
            transition: "all 0.3s",
            position: "relative",
          }}
        >
          <Card
            title="To-Do List"
            style={{ marginBottom: "10px" }}
            extra={[
              <Tooltip title="Sort By">
                <Dropdown menu={{ items, selectable: true }}>
                  <Button icon={<SortAscendingOutlined />}></Button>
                </Dropdown>
              </Tooltip>,
              <Divider type="vertical" />,
              <Tooltip title="New">
                <Button
                  icon={<PlusOutlined />}
                  onClick={() =>
                    setTodoModalStatus({
                      open: true,
                      create: true,
                      update: false,
                      payload: null,
                    })
                  }
                ></Button>
              </Tooltip>,
            ]}
          >
            <Progress percent={completionRate} status="active" />
            <List
              itemLayout="horizontal"
              size="small"
              locale={{ emptyText: "Nothing To do" }}
              dataSource={todoList.filter((task) => !task.done)}
              renderItem={(task) => (
                <List.Item key={task._id}>
                  <List.Item.Meta
                    title={task.description}
                    description={
                      <>
                        {task.dueDate &&
                          dayjs(task.dueDate).format("MM/DD/YYYY")}
                        <Divider type="vertical" />
                        <Tag
                          color={
                            task.priority === "High"
                              ? "red"
                              : task.priority === "Medium"
                                ? "orange"
                                : "green"
                          }
                        >
                          {task.priority}
                        </Tag>
                      </>
                    }
                  />

                  <Tooltip title="Mark as Done">
                    <Button
                      color="danger"
                      style={{ border: "none" }}
                      onClick={() => completeTask(task._id)}
                      icon={<CheckCircleOutlined style={{ color: "green" }} />}
                    ></Button>
                  </Tooltip>
                </List.Item>
              )}
            />
          </Card>
          <ContactList
            contacts={contactList}
            showAdditionalInfo={true}
            filterContacts={(search) => {
              if (search) {
                idClient && getListData("contacts", idClient, search);
              } else {
                resetContacts();
              }
            }}
            idClient={idClient}
            next={() => {
              idClient && getListData("contacts", idClient);
            }}
          />
        </Col>
      </Row>
      <DrawerCreateOrUpdateProject
        modalStatus={projectModal}
        idClient={idClient || ""}
        onSubmit={(values) => {
          if (isDemoEnv) {
            openModalDemo();
          } else {
            if (idClient) {
              createOrUpdateProject(values, idClient, values._id);
            }
          }
        }}
        handleChangeModalStatus={(payload) =>
          handleChangeProjectModalStatus({
            ...projectModal,
            payload: payload || projectModal?.payload,
            create: false,
            update: true,
          } as any)
        }
        handleCreateSource={(source) => {
          handleEditData({
            endpoint: `client/${idClient}`,
            newData: {
              newSource: source,
            },
            next: () => {
              idClient && getClientInformation(idClient, () => {});
            },
            showAlert: false,
          });
        }}
        handleCreateLocation={(locationName) => {
          handleEditData({
            endpoint: `client/${idClient}`,
            newData: {
              newLocation: locationName,
            },
            next: () => {
              idClient && getClientInformation(idClient, () => {});
            },
          });
        }}
        handleCreateNewProjectStatus={(projectStatusName) => {
          handleEditData({
            endpoint: `client/${idClient}`,
            newData: {
              newProjectStatus: projectStatusName,
            },
            next: () => {
              idClient && getClientInformation(idClient, () => {});
            },
            showAlert: false,
          });
        }}
        onCancel={() => {
          closeItemModal("project");
          idClient &&
            getListData("tasks", idClient, undefined, {
              assignedTo: true,
              active: true,
            });
        }}
      />
      <ModalCreateOrUpdateToDo
        modalStatus={todoModalStatus}
        onCancel={() => {
          setTodoModalStatus(null);
        }}
        onSubmit={(values) => {
          idClient &&
            handleCreateData({
              endpoint: "todos",
              idClient,
              data: values,
              next: () => {
                getListData("todos", idClient);
                setTodoModalStatus(null);
              },
              showAlert: false,
            });
        }}
      />
      {idClient && (
        <CustomerDrawer
          open={customerDrawerStatus.open}
          idClient={idClient}
          idCustomer={customerDrawerStatus.idCustomer}
          onSelectOne={(record: CustomerProps) =>
            setCustomerDrawerStatus({ open: true, idCustomer: record._id })
          }
          onClose={() => {
            Promise.all([
              getListData("tasks", idClient, undefined, {
                assignedTo: true,
                active: true,
              }),
              getListData("activities-paginated", idClient, undefined, {
                assignedTo: true,
                active: true,
              }),
              getListData("projects-paginated", idClient, undefined, {
                assignedTo: profileInfo?._id,
                active: true,
              }),
            ]).finally(() => {
              setCustomerDrawerStatus({ open: false, idCustomer: null });
            });
          }}
        />
      )}
      <DemoModal />
    </>
  );
};
